<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import ApiConnection from "@/api";

import type { Headers, PaginationMeta } from "@/types/rest";
import type { Activity } from "@/types/activity";

import { activitiesStore } from "@/stores/activities";
import { addErrorBottom } from "@/stores/messages";

import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { useDataHelper } from "@/mixins/dataHelper";
import { getPaginationLimit } from "@/mixins/listing";

import ActivityListItem from "@/components/activity/ActivityListItem.vue";
import ListPaging from "@/components/ui/ListPaging.vue";

const activitiesApi = ApiConnection.activities;

const router = useRouter();
const { sleep } = useTiming();
const { objectsDeepEqual } = useDataHelper();
useComponentErrorCapture();

const activitiesStorage = activitiesStore();

const props = defineProps({
  filterText: {
    type: String,
    default: '',
  },
  pagination: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["add", "clone", "loaded"]);

const activities = ref<Activity[] | []>([]);
const pagination = ref<PaginationMeta>(
  Object.keys(props.pagination).length > 0 ?
  props.pagination as PaginationMeta :
  { limit: getPaginationLimit() ?? 20, page: 1 }
);

const helperFilterText = ref(props.filterText);
const hasActivity = ref(true);
const fetching = ref(false);

activitiesStorage.$subscribe(async (mutation, state) => {
  await sleep(1000);
  if (!objectsDeepEqual(state.activitiesByPage.activities, activities.value)) {
    try {
      await fetchActivities();
    } catch (e: any) {
      addErrorBottom(e);
    }
  }
});

watch(() => props.filterText, async (newVal) => {
  if (helperFilterText.value !== newVal) {
    pagination.value.page = 1;
    
    try {
      await fetchActivities();
    } catch (e: any) {
      addErrorBottom(e);
    }
    helperFilterText.value = newVal;
  }
});

watch(
  pagination,
  async (val) => {
    if (val.count == 0 && props.filterText.length == 0) {
      hasActivity.value = false;
    } else if (val.count == 0) {
      const activityNumber = await activitiesApi.getItemNumber();
      hasActivity.value = activityNumber > 0;
    } else {
      hasActivity.value = true;
    }
  },
  { deep: true }
);

async function fetchActivities() {
  fetching.value = true;
  try {
    const actiList = await activitiesStorage.getActivitiesByPageByLimit(pagination.value.page, pagination.value.limit, props.filterText);
    if (actiList && actiList.data) {
      activities.value = actiList.data.activities;

      const actiHeaders = actiList.headers as Headers;
      if (actiHeaders) {
        const headerLimit = Number(actiHeaders['pagination-limit']);
        const newLimit = headerLimit <= Number(actiHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
        pagination.value = {
          count: Number(actiHeaders['pagination-count']) || 0,
          limit: newLimit || 0,
          page: Number(actiHeaders['pagination-page']) || 0,
        }
      }
    }
    emit('loaded', pagination.value);
  } catch (e: any) {
    addErrorBottom(e);
  }
  fetching.value = false;
}

function goToItem(id: string) {
  router.push({
    name: "activity",
    params: { activity_id: id },
    query: { category: 'activity' },
  });
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchActivities();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchActivities();
}

function addItem() {
  emit('add');
}

function cloneItem(activity: Activity) {
  emit('clone', activity);
}

async function deleteItem(activity: Activity) {
  try {
    const lastActiveListItem = (activities.value.length == 1 && pagination.value.page > 1);
    await activitiesStorage.delete(activity);
    
    if (lastActiveListItem) {
      changePage(pagination.value.page - 1);
    } else {
      fetchActivities();
    }
  } catch (e: any) {
    addErrorBottom(e);
  }
}

onMounted(() => {
  fetchActivities();
  helperFilterText.value = props.filterText;
})

</script>
<template>
  <div class="ent-list">
    <div v-if="activities && activities.length > 0" class="ent-list__body-wrapper" v-overflow-indicator>
      <div
        class="ent-list__body">
        <ActivityListItem
          v-for="activity in activities"
          @goto="goToItem(activity.entity_id)"
          @clone="cloneItem(activity)"
          @delete="deleteItem(activity)"
          :key="activity.entity_id"
          :activity="activity"
        />
      </div>
    </div>
    <ListPaging
      v-if="activities && activities.length > 0"
      @change-page="changePage"
      @change-per-page="changePerPage"
      :pagination="pagination || {}"
      :length="activities.length"
    />
    <p v-if="!fetching && activities.length < 1 && hasActivity" class="ent-list__description">{{ $t('ui.nothing_was_found') }}</p>
    <div v-else-if="!fetching && activities.length < 1" class="ent-list__no-items">
      <img alt="Add activity" src="/images/add-page.svg" />
      <h3>{{ $t('activity.add_first') }}</h3>
      <p>{{ $t('activity.no_activities') }}</p>
      <button
        @click="addItem"
        class="button button--primary button--hybrid button--hybrid--reversed">
        {{ $t('ui.add') }}
        <i class="fa-light fa-plus"></i>
      </button>
    </div>
  </div>
</template>
